import { PREINFORME_COLORS } from "../constants/preinformeColors";
import { formatearFechaHistorial, textoClasificacionHistorial, SIN_DATO } from "./HelpMediciones";

/**
 * Extrae el unico elemento de preinforme de la respuesta paginada del
 * servicio, o null si no hay preinforme disponible o la respuesta indica
 * error.
 * @param {Object} response
 */
export function extractPreinforme(response) {
	if (!response || response.error === true) {
		return null;
	}
	if (!Array.isArray(response.content) || response.content.length === 0) {
		return null;
	}
	return response.content[0];
}

/**
 * Devuelve el valor mostrable de un campo del preinforme, o "--" cuando
 * el valor es null, undefined o cadena vacia.
 * @param {*} value
 */
export function showValue(value) {
	if (value === null || value === undefined || value === "") {
		return "--";
	}
	return value;
}

/**
 * Resuelve el nombre y el documento a mostrar en la seccion "Datos
 * personales" de la vista de preinforme. Prioriza el nombre y el documento
 * que llegan por location.state (navegacion desde la lista); si el state
 * no existe, esta vacio o no trae nombre, toma el documento de los params
 * de la ruta y muestra "--" como nombre.
 * @param {Object} state location.state (puede ser undefined)
 * @param {Object} params useParams() de la ruta (trae documento)
 */
export function resolvePersonalData(state, params) {
	const { nombre, documento } = state || {};
	if (nombre) {
		return { nombre, documento };
	}
	return { nombre: "--", documento: params?.documento };
}

/**
 * Extrae la url publica del PDF del informe desde la respuesta del
 * servicio, o null cuando la respuesta falta, indica error o no trae
 * una url utilizable.
 * @param {Object} response
 */
export function extractInformeUrl(response) {
	if (!response || response.error === true) {
		return null;
	}
	if (typeof response.url !== "string" || response.url === "") {
		return null;
	}
	return response.url;
}

/**
 * Parsea el campo payload (string con JSON serializado) de un preinforme y
 * devuelve siempre las mismas siete claves, con valores por defecto seguros
 * cuando payload es null, undefined, cadena vacia, no parseable, o cuando el
 * JSON parseado no trae alguna clave o la trae con un tipo distinto al
 * esperado. Nunca lanza excepcion.
 * @param {string} payload
 */
export function parsePreinformePayload(payload) {
	const defaults = {
		metricas: [],
		ausentes: [],
		fortalezas: [],
		oportunidades: [],
		recomendaciones: [],
		metas12Semanas: null,
		advertencias: [],
	};

	if (typeof payload !== "string" || payload === "") {
		return defaults;
	}

	let parsed;
	try {
		parsed = JSON.parse(payload);
	} catch (e) {
		return defaults;
	}

	if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
		return defaults;
	}

	const result = { ...defaults };
	["metricas", "ausentes", "fortalezas", "oportunidades", "recomendaciones", "advertencias"].forEach((clave) => {
		if (Array.isArray(parsed[clave])) {
			result[clave] = parsed[clave];
		}
	});
	if (parsed.metas12Semanas && typeof parsed.metas12Semanas === "object" && !Array.isArray(parsed.metas12Semanas)) {
		result.metas12Semanas = parsed.metas12Semanas;
	}
	return result;
}

/**
 * Formatea el valor numerico de una metrica a un decimal, sin decimal
 * sobrante cuando el redondeo es entero. Devuelve "--" cuando valor no es
 * un numero finito.
 * @param {number} valor
 */
export function formatValorMetrica(valor) {
	if (typeof valor !== "number" || !Number.isFinite(valor)) {
		return "--";
	}
	const redondeado = Math.round(valor * 10) / 10;
	return String(redondeado);
}

/**
 * Calcula, por cada segmento de una metrica, el ancho en porcentaje de su
 * tramo en la barra de rangos (finPct - inicioPct, recortado a [0, 100]).
 * Devuelve [] cuando segmentos no es un arreglo no vacio.
 * @param {Array} segmentos
 */
export function buildSegmentosBarra(segmentos) {
	if (!Array.isArray(segmentos) || segmentos.length === 0) {
		return [];
	}
	return segmentos.map((segmento) => {
		const inicioPct = typeof segmento?.inicioPct === "number" ? segmento.inicioPct : 0;
		const finPct = typeof segmento?.finPct === "number" ? segmento.finPct : 0;
		let anchoPct = finPct - inicioPct;
		if (anchoPct < 0) {
			anchoPct = 0;
		}
		if (anchoPct > 100) {
			anchoPct = 100;
		}
		return {
			etiqueta: segmento?.etiqueta,
			severidad: segmento?.severidad,
			anchoPct,
		};
	});
}

/**
 * Valida la posicion del valor de una metrica sobre la barra de rangos.
 * Devuelve el mismo numero cuando es finito y esta en [0, 100], o null en
 * cualquier otro caso (dato ausente, no numerico o fuera de rango).
 * @param {number} posicionPct
 */
export function resolvePosicionPct(posicionPct) {
	if (typeof posicionPct !== "number" || !Number.isFinite(posicionPct)) {
		return null;
	}
	if (posicionPct < 0 || posicionPct > 100) {
		return null;
	}
	return posicionPct;
}

/**
 * Devuelve el color de la paleta local asociado a un nivel de severidad del
 * payload (0 = optimo, 1 = leve, 2 = advertencia, 3 = grave). Cualquier otro
 * valor devuelve el color neutro.
 * @param {number} severidad
 */
export function colorSeveridad(severidad) {
	if (Object.prototype.hasOwnProperty.call(PREINFORME_COLORS.severidad, severidad)) {
		return PREINFORME_COLORS.severidad[severidad];
	}
	return PREINFORME_COLORS.severidad.neutro;
}

/**
 * Nombres legibles de los indicadores de metrica que puede traer el
 * payload. Un codigo que no este en el mapa se devuelve tal cual, para que
 * el backend pueda anadir indicadores nuevos sin romper la vista.
 */
const INDICADOR_LABELS = {
	IMC: "IMC",
	IGC: "Grasa corporal",
	MUSCULO: "Músculo esquelético",
	VISCERAL: "Grasa visceral",
	PRESION: "Presión arterial",
	FRECUENCIA: "Frecuencia cardíaca",
	SPO2: "Saturación de oxígeno",
};

/**
 * Devuelve el nombre legible de un codigo de indicador, o el propio codigo
 * cuando no esta en el mapa.
 * @param {string} codigo
 */
export function labelIndicador(codigo) {
	return INDICADOR_LABELS[codigo] || codigo;
}

/**
 * Tabla interna de campos planos del preinforme que pueden mostrarse como
 * tarjeta de valor sin barra de rangos. No incluye alcoholemia: ese dato no
 * llega en este servicio.
 */
const CAMPOS_PLANOS = [
	{ clave: "peso", campo: "peso", etiqueta: "Peso corporal", unidad: "kg", indicador: null },
	{ clave: "imc", campo: "imc", etiqueta: "IMC", unidad: "kg/m²", indicador: "IMC" },
	{ clave: "grasaCorporal", campo: "grasaCorporal", etiqueta: "Grasa corporal", unidad: "%", indicador: "IGC" },
	{ clave: "musculoEsqueletico", campo: "musculoEsqueletico", etiqueta: "Músculo esquelético", unidad: "%", indicador: "MUSCULO" },
	{ clave: "grasaVisceral", campo: "grasaVisceral", etiqueta: "Grasa visceral", unidad: "nivel", indicador: "VISCERAL" },
	{ clave: "tmbKcal", campo: "tmbKcal", etiqueta: "TMB", unidad: "kcal", indicador: null },
	{ clave: "getKcal", campo: "getKcal", etiqueta: "GET", unidad: "kcal", indicador: null },
	{ clave: "presion", campo: "sistolica", etiqueta: "Presión arterial", unidad: "mmHg", indicador: "PRESION" },
	{ clave: "frecuenciaCardiaca", campo: "frecuenciaCardiaca", etiqueta: "Frecuencia cardíaca", unidad: "lpm", indicador: "FRECUENCIA" },
	{ clave: "saturacionOxigeno", campo: "saturacionOxigeno", etiqueta: "Saturación de oxígeno", unidad: "%", indicador: "SPO2" },
];

/**
 * Compone la lectura de presion arterial a partir de los campos planos del
 * preinforme: "127/73" con ambos valores presentes, "127/--" cuando falta
 * la diastolica (null, undefined o cadena vacia), o null cuando falta la
 * propia sistolica. Nunca lanza excepcion.
 * @param {Object} preinforme
 */
export function resolveValorPresion(preinforme) {
	if (!preinforme) {
		return null;
	}
	const sistolica = preinforme.sistolica;
	if (sistolica === null || sistolica === undefined || sistolica === "") {
		return null;
	}
	const diastolica = preinforme.diastolica;
	const diastolicaTexto = diastolica === null || diastolica === undefined || diastolica === "" ? "--" : diastolica;
	return `${sistolica}/${diastolicaTexto}`;
}

/**
 * Resuelve el valor a mostrar de un campo plano del preinforme. El caso
 * especial es "presion", que delega en resolveValorPresion.
 * @param {Object} campoPlano fila de CAMPOS_PLANOS
 * @param {Object} preinforme
 */
function resolveValorCampoPlano(campoPlano, preinforme) {
	if (campoPlano.clave === "presion") {
		return resolveValorPresion(preinforme);
	}
	return preinforme[campoPlano.campo];
}

/**
 * Construye las tarjetas de valor (sin barra de rangos) de los campos
 * planos del preinforme, excluyendo los campos cuyo indicador ya aparece en
 * metricas y los campos sin valor presente.
 * @param {Object} preinforme
 * @param {Array} metricas payloadData.metricas
 */
export function buildTarjetasValor(preinforme, metricas) {
	if (!preinforme) {
		return [];
	}
	const indicadoresConMetrica = new Set(
		Array.isArray(metricas) ? metricas.map((metrica) => metrica && metrica.indicador) : []
	);
	return CAMPOS_PLANOS
		.filter((campoPlano) => !(campoPlano.indicador && indicadoresConMetrica.has(campoPlano.indicador)))
		.map((campoPlano) => ({ campoPlano, valor: resolveValorCampoPlano(campoPlano, preinforme) }))
		.filter(({ valor }) => valor !== null && valor !== undefined && valor !== "")
		.map(({ campoPlano, valor }) => ({
			clave: campoPlano.clave,
			etiqueta: campoPlano.etiqueta,
			valor,
			unidad: campoPlano.unidad,
		}));
}

// Tabla de las tarjetas de mediciones del preinforme (feature 20): unica
// fuente de orden, titulos, unidades y campos del objeto `mediciones`.
const MEDICIONES_PREINFORME = [
	{
		clave: "glucometria", etiqueta: "Glucometría", campoFecha: "created",
		principal: { campo: "glucometria", unidad: "mg/dL", campoClasificacion: "rangoValor" },
		filas: [],
	},
	{
		clave: "espirometria", etiqueta: "Espirometría", campoFecha: "datecreated",
		principal: { campo: "porcentaje", unidad: "%", campoClasificacion: "rangoValor" },
		filas: [
			{ clave: "pef", etiqueta: "PEF", campo: "pef", unidad: "L/min", campoClasificacion: null },
			{ clave: "pred", etiqueta: "PRED", campo: "pred", unidad: "L/min", campoClasificacion: null },
		],
	},
	{
		clave: "lipidograma", etiqueta: "Lipidograma", campoFecha: "created",
		principal: null,
		filas: [
			{ clave: "colesterol", etiqueta: "Colesterol", campo: "colesterol", unidad: "mg/dL", campoClasificacion: "rangoColesterol" },
			{ clave: "ldl", etiqueta: "LDL", campo: "ldl", unidad: "mg/dL", campoClasificacion: "rangoLdl" },
			{ clave: "hdl", etiqueta: "HDL", campo: "hdl", unidad: "mg/dL", campoClasificacion: "rangoHdl" },
			{ clave: "trigliceridos", etiqueta: "Triglicéridos", campo: "trigliceridos", unidad: "mg/dL", campoClasificacion: "rangoTrigliceridos" },
		],
	},
	{
		clave: "perimetroAbdominal", etiqueta: "Perímetro abdominal", campoFecha: "created",
		principal: { campo: "perimetro", unidad: "cm", campoClasificacion: "clasificacion" },
		filas: [],
	},
];

function esObjetoPlano(valor) {
	return valor !== null && typeof valor === "object" && !Array.isArray(valor);
}

/**
 * Normaliza el objeto `mediciones` del preinforme a cuatro descriptores de
 * tarjeta (Glucometría, Espirometría, Lipidograma, Perímetro abdominal), en
 * ese orden. Un módulo ausente (null, clave faltante o tipo distinto) da un
 * descriptor `atenuada: true`. Devuelve [] cuando `mediciones` no es un
 * objeto. No muta la entrada ni lanza excepción.
 * @param {Object} mediciones
 */
export function buildTarjetasMediciones(mediciones) {
	if (!esObjetoPlano(mediciones)) {
		return [];
	}
	const conUnidad = (valor, unidad) => (valor === "--" ? null : unidad);
	return MEDICIONES_PREINFORME.map((config) => {
		const modulo = mediciones[config.clave];
		if (!esObjetoPlano(modulo)) {
			return {
				clave: config.clave,
				etiqueta: config.etiqueta,
				atenuada: true,
				valor: null,
				unidad: null,
				clasificacion: null,
				fecha: null,
				filas: [],
			};
		}
		const principal = config.principal;
		const valorPrincipal = principal ? formatValorMetrica(modulo[principal.campo]) : null;
		const fecha = formatearFechaHistorial(modulo[config.campoFecha]);
		return {
			clave: config.clave,
			etiqueta: config.etiqueta,
			atenuada: false,
			valor: valorPrincipal,
			unidad: principal ? conUnidad(valorPrincipal, principal.unidad) : null,
			clasificacion: principal ? textoClasificacionHistorial(modulo[principal.campoClasificacion]) : null,
			fecha: fecha === SIN_DATO ? "--" : fecha,
			filas: config.filas.map((fila) => {
				const valorFila = formatValorMetrica(modulo[fila.campo]);
				return {
					clave: fila.clave,
					etiqueta: fila.etiqueta,
					valor: valorFila,
					unidad: conUnidad(valorFila, fila.unidad),
					clasificacion: fila.campoClasificacion
						? textoClasificacionHistorial(modulo[fila.campoClasificacion])
						: null,
				};
			}),
		};
	});
}

/**
 * Ordena una lista narrativa del payload (oportunidades, fortalezas,
 * recomendaciones) de forma ascendente por su campo orden, sin mutar el
 * arreglo recibido. Los elementos sin orden numerico van al final,
 * conservando entre ellos el orden de entrada (Array.prototype.sort es
 * estable en Node 14 y en los navegadores objetivo). Devuelve [] cuando
 * lista no es un arreglo.
 * @param {Array} lista
 */
export function normalizarListaNarrativa(lista) {
	if (!Array.isArray(lista)) {
		return [];
	}
	const esOrdenValido = (item) => typeof item?.orden === "number" && Number.isFinite(item.orden);
	return lista.slice().sort((a, b) => {
		const aValido = esOrdenValido(a);
		const bValido = esOrdenValido(b);
		if (!aValido && !bValido) {
			return 0;
		}
		if (!aValido) {
			return 1;
		}
		if (!bValido) {
			return -1;
		}
		return a.orden - b.orden;
	});
}

/**
 * Tabla de etiquetas legibles de las cinco metas a 12 semanas que entrega
 * el backend, en el orden en el que se muestran en el panel.
 */
const METAS_LABELS = [
	{ clave: "peso", etiqueta: "Peso objetivo" },
	{ clave: "imc", etiqueta: "IMC objetivo" },
	{ clave: "grasa", etiqueta: "Grasa corporal" },
	{ clave: "musculo", etiqueta: "Músculo esquelético" },
	{ clave: "actividad", etiqueta: "Actividad física" },
];

/**
 * Resuelve las cinco metas a 12 semanas del payload en el orden fijo peso,
 * imc, grasa, musculo, actividad, con "--" en las claves ausentes o vacias.
 * El valor se devuelve tal cual llega del backend, sin concatenar unidades.
 * Devuelve [] cuando metas no es un objeto plano o cuando ninguna de las
 * cinco claves trae un valor utilizable (distinto de null, undefined y
 * cadena vacia).
 * @param {Object} metas
 */
export function resolveMetas12Semanas(metas) {
	if (!metas || typeof metas !== "object" || Array.isArray(metas)) {
		return [];
	}
	const esUtilizable = (valor) => valor !== null && valor !== undefined && valor !== "";
	const hayAlgunValor = METAS_LABELS.some(({ clave }) => esUtilizable(metas[clave]));
	if (!hayAlgunValor) {
		return [];
	}
	return METAS_LABELS.map(({ clave, etiqueta }) => ({
		clave,
		etiqueta,
		valor: esUtilizable(metas[clave]) ? metas[clave] : "--",
	}));
}

/**
 * Devuelve unicamente las cadenas no vacias del arreglo de advertencias del
 * payload. Devuelve [] cuando advertencias no es un arreglo o no contiene
 * ninguna cadena no vacia.
 * @param {Array} advertencias
 */
export function normalizarAdvertencias(advertencias) {
	if (!Array.isArray(advertencias)) {
		return [];
	}
	return advertencias.filter((item) => typeof item === "string" && item !== "");
}
